import { Currency, Lang } from './../types';
import store from './../store';

declare global {
  interface FormatProps {
    lang?: string;
    currency?: string;
    decimals?: number;
    isCent?: boolean;
    round?: boolean;
    roundUp?: boolean;
    compact?: boolean;
  }

  interface Number {
    localize: () => string;

    millisecondsToSec: () => number;

    formatCurrency: (props?: FormatProps) => string;

    formatCent: () => number;

    formatDecimalOneSign: () => string;

    formatByLocaleAndCurrency: (
      lang: string,
      currency: string,
      digits: Object,
      frontString?: string,
      compact?: boolean,
    ) => string;

    roundCent: () => number;
  }
}

Number.prototype.formatCent = function (): number {
  return this / 100;
};

Number.prototype.localize = function (): string {
  const DEFAULT_LANG = Lang.DEFAULT_LANG();

  const lang = store.state.support.lang.locale || DEFAULT_LANG.locale;

  return this.toLocaleString(lang);
};

Number.prototype.formatCurrency = function (props: FormatProps = {}): string {
  const DEFAULT_LANG = Lang.DEFAULT_LANG();
  const DEFAULT_CURRENCY = Currency.DEFAULT_CURRENCY();

  let {
    lang = store.state.support.lang.locale || DEFAULT_LANG.locale,
    currency = store.state.support.currency.currency ||
      DEFAULT_CURRENCY.currency,
    decimals = 2,
    round = false,
    roundUp = false,
    isCent = true,
    compact = false,
  } = props;

  let value = isCent ? this.formatCent() : this;

  decimals = roundUp || round ? 0 : decimals;

  if (round && !roundUp) {
    value = Math.round(value);
  } else if (roundUp) {
    value = Math.ceil(value);
  }

  if (value < 0) {
    const pos = Math.abs(value);
    return pos.formatByLocaleAndCurrency(
      lang,
      currency,
      decimals,
      '–',
      compact,
    );
  } else {
    return value.formatByLocaleAndCurrency(
      lang,
      currency,
      decimals,
      null,
      compact,
    );
  }
};

Number.prototype.formatByLocaleAndCurrency = function (
  lang: string,
  currency: string,
  digits: number,
  frontString?: string,
  compact?: boolean,
): string {
  let value = '';
  const compactProps = compact
    ? { notation: 'compact', compactDisplay: 'short' }
    : null;

  if (lang === Lang.LANGS.UK && currency === Currency.CURRENCIES.PLN) {
    value = `${this.toLocaleString(lang, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      ...compactProps,
    })} zł`;
  } else if (currency === Currency.CURRENCIES.USD) {
    if (lang === Lang.LANGS.UK || lang === Lang.LANGS.US) {
      value = `$${this.toLocaleString(lang, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        ...compactProps,
      })}`;
    } else {
      value = `${this.toLocaleString(lang, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        ...compactProps,
      })} $`;
    }
  } else if (currency === Currency.CURRENCIES.EUR) {
    value = this.toLocaleString(Lang.LANGS.UK, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      ...compactProps,
    });
  } else if (currency === Currency.CURRENCIES.HUF) {
    value = Math.ceil(this).toLocaleString(lang, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...compactProps,
    } as any);
  } else
    value = this.toLocaleString(lang, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      ...compactProps,
    });

  return (frontString ? frontString : '') + value;
};

Number.prototype.formatDecimalOneSign = function (): string {
  return parseFloat(this).toFixed(2);
};

Number.prototype.millisecondsToSec = function (): number {
  return +(this / 1000).toFixed(2);
};

Number.prototype.roundCent = function (): number {
  return Math.floor(this.formatCent());
};
